import React, { useEffect, useState } from 'react';
import {
  CODEBUILD_BUILD_NUMBER,
  OPEN_REPLAY_INGEST_POINT,
  OPEN_REPLAY_PROJECT_KEY,
} from "api-config";
import { isBrowser } from './Browser';
import Tracker from '@openreplay/tracker';
import { useSessionContext } from "./Session";
import log from 'loglevel';


// Mirrors LogRocketWrapper. Initializes the OpenReplay tracker once on
// mount, then re-runs the identify effect whenever the session context
// changes so the right user is tagged on the recording.
//
// The tracker is held in state (not a ref) so that the identify effect
// re-runs as soon as `tracker.start()` resolves. This avoids the race
// where a user logs in before start() is finished — without the state
// trigger, the identify effect would have already fired with `tracker`
// still null and never re-fire.
const OpenReplayWrapper = () => {
  const [tracker, setTracker] = useState(null);
  const { sessionContext } = useSessionContext();
  log.debug(`OpenReplayWrapper: sessionContext`, sessionContext);
  const { isAdminUser, session } = sessionContext || {};
  const { user } = session || {};
  log.debug(`OpenReplayWrapper: user`, user);
  const { email, name } = user || {};

  // Init the tracker once on mount. Subsequent renders are no-ops because
  // the dependency array is empty.
  useEffect(() => {
    if (!isBrowser()) {
      log.info("OpenReplay: not initializing due to no browser detected.");
      return;
    }
    if (!OPEN_REPLAY_PROJECT_KEY) {
      log.info("OpenReplay: not initializing — project key is empty.");
      return;
    }
    log.info(
      `OpenReplay::init() projectKey=${OPEN_REPLAY_PROJECT_KEY} ingestPoint=${OPEN_REPLAY_INGEST_POINT} build=${CODEBUILD_BUILD_NUMBER}`
    );
    const t = new Tracker({
      projectKey: OPEN_REPLAY_PROJECT_KEY,
      ingestPoint: OPEN_REPLAY_INGEST_POINT,
      revID: CODEBUILD_BUILD_NUMBER,
    });
    // tracker.start() returns a Promise. If the ingest server is
    // unreachable or the project key is invalid — both possible during
    // the phased rollout — the rejection would be unhandled. Catch it
    // and only expose the tracker via state if start() actually
    // succeeded, so the identify effect below never operates on a
    // half-initialized tracker.
    t.start()
      .then(() => {
        log.info("OpenReplay: tracker started");
        setTracker(t);
      })
      .catch((err) => {
        const reason = err && err.message ? err.message : String(err);
        log.warn(`OpenReplay: tracker.start() failed — ${reason}`);
      });
    // No cleanup: the tracker is intended to live for the lifetime of
    // the page, and the SDK does not currently expose a documented
    // unmount/stop API.
  }, []);

  // Identify (or de-identify) the user when the session context changes
  // OR when the tracker becomes available after start() resolves.
  useEffect(() => {
    if (!isBrowser() || !tracker) {
      return;
    }
    if (email) {
      log.info(`OpenReplay.setUserID(${email})`);
      tracker.setUserID(email);
      tracker.setMetadata("name", name || "");
      tracker.setMetadata("isAdminUser", String(Boolean(isAdminUser)));
    } else {
      // Logged out — clear the user ID AND every metadata key we set
      // above. OpenReplay has no "clear all metadata" API; values
      // persist across the session until each key is explicitly
      // overwritten, so failing to clear them here would leak the
      // previous user's name and admin status onto subsequent
      // recordings.
      log.debug("OpenReplay: clearing user ID and metadata");
      tracker.setUserID("");
      tracker.setMetadata("name", "");
      tracker.setMetadata("isAdminUser", "");
    }
  }, [email, name, isAdminUser, tracker]);

  return (<></>);
};

export {
  OpenReplayWrapper
};
